import { useRef, useMemo } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import * as THREE from 'three'

function HelixStrand({ offset = 0, color = '#5ed29c' }: { offset?: number; color?: string }) {
  const pointsRef = useRef<THREE.Points>(null)
  const count = 100
  
  const [positions, colors] = useMemo(() => {
    const positions = new Float32Array(count * 3)
    const colors = new Float32Array(count * 3)
    const colorObj = new THREE.Color(color)
    
    for (let i = 0; i < count; i++) {
      const t = i / count
      const angle = t * Math.PI * 8 + offset
      const radius = 1.5
      
      positions[i * 3] = Math.cos(angle) * radius
      positions[i * 3 + 1] = (t - 0.5) * 10
      positions[i * 3 + 2] = Math.sin(angle) * radius
      
      colors[i * 3] = colorObj.r
      colors[i * 3 + 1] = colorObj.g
      colors[i * 3 + 2] = colorObj.b
    }
    
    return [positions, colors]
  }, [offset, color])

  useFrame((state) => {
    if (pointsRef.current) {
      pointsRef.current.rotation.y = state.clock.elapsedTime * 0.1
    }
  })

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          args={[positions, 3]}
          count={count}
          array={positions}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-color"
          args={[colors, 3]}
          count={colors.length / 3}
          array={colors}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial size={0.08} vertexColors transparent opacity={0.8} />
    </points>
  )
}

function ConnectionLines({ offset1 = 0, offset2 = Math.PI }: { offset1?: number; offset2?: number }) {
  const linesRef = useRef<THREE.LineSegments>(null)
  
  const connectionPositions = useMemo(() => {
    const positions = []
    const count = 50
    
    for (let i = 0; i < count; i++) {
      const t = i / count
      const angle1 = t * Math.PI * 8 + offset1
      const angle2 = t * Math.PI * 8 + offset2
      const radius = 1.5
      
      const x1 = Math.cos(angle1) * radius
      const y1 = (t - 0.5) * 10
      const z1 = Math.sin(angle1) * radius
      
      const x2 = Math.cos(angle2) * radius
      const y2 = y1
      const z2 = Math.sin(angle2) * radius
      
      positions.push(x1, y1, z1, x2, y2, z2)
    }
    
    return new Float32Array(positions)
  }, [offset1, offset2])

  useFrame((state) => {
    if (linesRef.current) {
      linesRef.current.rotation.y = state.clock.elapsedTime * 0.1
    }
  })

  return (
    <lineSegments ref={linesRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          args={[connectionPositions, 3]}
          count={connectionPositions.length / 3}
          array={connectionPositions}
          itemSize={3}
        />
      </bufferGeometry>
      <lineBasicMaterial color="rgba(79, 216, 255, 0.3)" transparent opacity={0.3} />
    </lineSegments>
  )
}

export function DNAHelix() {
  return (
    <div className="w-full h-full">
      <Canvas camera={{ position: [0, 0, 8], fov: 50 }} dpr={[1, 1.5]}>
        <ambientLight intensity={0.3} />
        <HelixStrand offset={0} color="#5ed29c" />
        <HelixStrand offset={Math.PI} color="#4fd8ff" />
        <ConnectionLines />
      </Canvas>
    </div>
  )
}